import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpParams, HttpProgressEvent } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { last, lastValueFrom, Observable, tap } from "rxjs";
import { concatMap } from "rxjs/operators";
import { HttpMethod } from "./http-method";
import { HTTP_SERVICE_URL } from "./injection-tokens";
import { ResponseHandler } from "./response-handler";


@Injectable({
    providedIn: 'root'
})
export class Endpoint {

    constructor(
        private http: HttpClient,
        private responseHandler: ResponseHandler,
        @Inject(HTTP_SERVICE_URL) private readonly serviceUrl: string,
    ) {
    }

    public get<TResponse>(url: string, responseType: 'arraybuffer' | 'blob' | 'text' | 'json' = 'json', queryParams?: HttpParams): Observable<TResponse> {
        return this.rawRequest<TResponse, null>('GET', url, null, responseType, queryParams)
            .pipe(
                concatMap((response: TResponse) => this.responseHandler.handleSuccess(response, 'GET'))
            );
    }

    public getWithToken<TResponse>(url: string, token: string): Observable<TResponse> {
        const headerSettings: { [name: string]: string | string[] } = {};
        headerSettings['Content-Type'] = 'application/json';
        headerSettings['Authorization'] = 'Bearer ' + token;
        const headers = new HttpHeaders(headerSettings);

        return this.http.get<TResponse>(this.serviceUrl + url, { headers });
    }

    public post<TResponse, TRequest>(url: string, data: TRequest, responseType: 'arraybuffer' | 'blob' | 'text' | 'json' = 'json'): Observable<TResponse> {
        return this.rawRequest<TResponse, TRequest>('POST', url, data, responseType)
            .pipe(
                concatMap((response: TResponse) => this.responseHandler.handleSuccess(response, 'POST'))
            );
    }

    public put<TResponse, TRequest>(url: string, data: TRequest, responseType: 'arraybuffer' | 'blob' | 'text' | 'json' = 'json'): Observable<TResponse> {
        return this.rawRequest<TResponse, TRequest>('PUT', url, data, responseType)
            .pipe(
                concatMap((response: TResponse) => this.responseHandler.handleSuccess(response, 'PUT'))
            );
    }

    public patch<TResponse, TRequest>(url: string, data: TRequest, responseType: 'arraybuffer' | 'blob' | 'text' | 'json' = 'json'): Observable<TResponse> {
        return this.rawRequest<TResponse, TRequest>('PATCH', url, data, responseType)
            .pipe(
                concatMap((response: TResponse) => this.responseHandler.handleSuccess(response, 'PATCH'))
            );
    }

    public delete<TResponse, TRequest>(url: string, data?: TRequest, responseType: 'arraybuffer' | 'blob' | 'text' | 'json' = 'json'): Observable<TResponse> {
        return this.rawRequest<TResponse, unknown>('DELETE', url, data, responseType)
            .pipe(
                concatMap((response: TResponse) => this.responseHandler.handleSuccess(response, 'DELETE'))
            );
    }

    public postWithProgress<TResponse, TRequest>(url: string, data: TRequest, responseType: 'arraybuffer' | 'blob' | 'text' | 'json' = 'json'): Observable<HttpEvent<TResponse>> {
        return this.rawRequestWithProgress<TResponse, TRequest>('POST', url, data, responseType)
            .pipe(
                concatMap((response: HttpEvent<TResponse>) => this.responseHandler.handleSuccess(response, 'POST'))
            );
    }

    public readonly postWithProgressAsPromise = <TResponse, TRequest>(
        url: string,
        data: TRequest,
        handlers: {
            onUploadProgress: (event: HttpProgressEvent) =>void,
        }
    ) => {
        const res$ = this.postWithProgress<TResponse, TRequest>(url, data, "json").pipe(
            tap(event => {
                switch (event.type) {
                    case HttpEventType.UploadProgress:
                        handlers.onUploadProgress(event);
                        break;
                    case HttpEventType.ResponseHeader:
                    case HttpEventType.Response:
                    case HttpEventType.DownloadProgress:
                    case HttpEventType.Sent:
                    case HttpEventType.User:
                        break;
                }
            }),
            last(),
        );

        return lastValueFrom(res$);
    };

    private rawRequest<TResponse, TRequest>(
        method: HttpMethod,
        url: string,
        requestData: TRequest,
        responseType: 'arraybuffer' | 'blob' | 'text' | 'json' = 'json', queryParams?: HttpParams): Observable<TResponse> {

        const options = {
            body: requestData,
            responseType: responseType as 'json',
            headers: undefined,
            params: queryParams
        };

        return this.http.request<TResponse>(method, this.serviceUrl + url, options);
    }

    private rawRequestWithProgress<TResponse, TRequest>(method: HttpMethod, url: string, requestData: TRequest,
        responseType: 'arraybuffer' | 'blob' | 'text' | 'json' = 'json', queryParams?: HttpParams): Observable<HttpEvent<TResponse>> {

        const options = {
            body: requestData,
            responseType: responseType as 'json',
            headers: undefined,
            params: queryParams
        };

        return this.http.request<TResponse>(
            method,
            this.serviceUrl + url,
            {
                ...options,
                reportProgress: true,
                observe: 'events',
            }
        );
    }
}
